import React, { useContext } from 'react';
import {
	faArrowLeft,
	faDotCircle,
	faEnvelope,
	faExclamationCircle,
	faFile,
	faFileInvoice,
	faHistory,
	faImages,
	faMapMarkerAlt,
	faPause,
	faPlay,
	faPlus,
	faRandom,
} from '@fortawesome/pro-regular-svg-icons';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react/hooks';
import { loader } from 'graphql.macro';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import {
	CaseStatus,
	GetSingleCase,
	GetSingleCaseVariables,
	UpdateCase,
	UpdateCaseVariables,
	Permissions,
	CreateWebDamageReport,
	CreateWebDamageReportVariables,
	ScalePointStatus,
} from '../../GraphQL';
import { DateTime } from 'luxon';
import { useFlag } from '@unleash/proxy-client-react';
import { FeatureFlagEnums } from '@ssg/common/FeatureFlagEnums';
import { GetWebCasesByErpNoQuery, useGetDuplicateCasesQuery, useGetTimeTrackingsInBcWithoutApprovalQuery, useMergeCasesMutation } from '@ssg/common/GraphQL/indexV2';
import { formatDateForInputEU } from '@ssg/common/Helpers/dateToDateOnlyString';
import { SelectOption } from '@ssg/common/Helpers/Helpers';
import { useWebCasesByErpNoQuery } from '@ssg/common/TimeRegistration/TimeRegHooks';
import { useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isCaseValidToClose } from '../../Components/Helpers/isCaseValidToClose';
import Modal, { ModalSize } from '@ssg/common/Components/Modal';
import GoogleMapsLink from '@ssg/common/Components/GoogleMapsLink';
import BoxContainer from '@ssg/common/Components/BoxContainer';
import Header from '@ssg/common/Components/Header';
import MovablesOverviewDownload from '../../Components/MovablesOverviewDownload';
import VisitationBox from './VisitationBox';
import CaseAgreementsBox from './CaseAgreementBox/CaseAgreementsBox';
import DebitorBox from './DebitorBox';
import DrivingSlipBox from './DrivingSlipBox/DrivingSlipBox';
import InjuredPartyBox from './InjuredPartyBox';
import MiscBox from './MiscBox/MiscBox';
import RiskEvaluationBox from './RiskEvaluation/RiskEvaluationBox';
import SSGInformationBox from './SSGInformationBox';
import Loading from '@ssg/common/Components/Loading';
import dateToDateTimeString from '@ssg/common/Helpers/dateToDateTimeString';
import CaseMail from './CaseMail';
import CreateJobModal from './CreateJobModal';
import CustomPolicyHolderBox from './CustomPolicyHolderBox';
import DamageBox from './DamageBox';
import ViewGuides from './ViewGuides';
import ScreeningBox from './Screening/ScreeningBox';
import AdministrativeProgress from '../../Components/AdministrativeProgress';
import FavoriteToggle from '../../Components/FavoriteToggle';
import ChangeTrack from './ChangeTrack';
import RestrictedArea from '@ssg/common/Components/RestrictedArea';
import UserContext from '../../UserContext';
import CaseChangeLog from './CaseChangeLog';
import ImportantInfoBox from './ImportantInfoBox';
import SkaforBox from './SkaforBox';
import AdviserReferrerBox from './AdviserReferrerBox';
import RelatedCatalogs from './RelatedCatalogs';
import RequisitionModal from '../Requisitions/RequisitionModal';
import CaseUsageBox from './CaseUsage/CaseUsageBox';
import MovablesOverviewModal from './MovablesOverviewModal';
import InvoiceModal from './Invoice/InvoiceModal';
import TextButton from '@ssg/common/Components/TextButton';
import DamageContactBox from './DamageContactBox';
import NoUser from '../NoUser';
import CloseCaseButton from './CloseCaseButton';
import ReopenCaseButton from './ReopenCaseButton';
import OperationProgress from '../../Components/OperationProgress';
import arraysHasMatchingValue from '@ssg/common/Helpers/arraysHasMatchingValue';
import Popover from '@ssg/common/Components/Popover';
import Table from '@ssg/common/Components/Table';
import Button from '@ssg/common/Components/Button';
import SearchableSelect from '@ssg/common/Components/SearchableSelect';
import { removeTypename } from '../../helper';
import EnvironmentVariableContext from '@ssg/common/EnvironmentVariableContext';
import ActiveUsersOnCaseIndicator from './ActiveUsersOnCaseIndicator';
import ConnectToScalepointButton from './ConnectToScalepointButton';

// GraphQL quries
const GET_CASE = loader('../../GraphQL/Cases/GetSingleCase.gql');
const UPDATE_CASE = loader('../../GraphQL/Cases/UpdateCase.gql');
const CREATE_DAMAGE_REPORT = loader('../../GraphQL/Reports/CreateWebDamageReport.gql');

const SingleCase: React.FC = (): React.ReactElement => {
	const userContext = React.useContext(UserContext);
	const isExternalUser = userContext.user?.external;
	const userPermissions = userContext.user?.permissions ?? [];
	const caseViewExternal = arraysHasMatchingValue(userPermissions, [Permissions.CASES_VIEW_EXTERNAL]);
	const envVar = useContext(EnvironmentVariableContext);

	const inspectionMovablesReportFlag = useFlag(FeatureFlagEnums.INSPECTION_MOVABLES_REPORT);
	const enableOfferModule = useFlag(FeatureFlagEnums.OFFER_MODULE);
	const showNonApprovedHoursOnCase = useFlag(FeatureFlagEnums.SHOW_NON_APPROVED_HOURS_ON_CASE);
	const asbestosReportFlag = useFlag(FeatureFlagEnums.ASBESTOS_REPORTS);
	const scalepointMergeCases = useFlag(FeatureFlagEnums.SCALEPOINT_MERGE_CASES);
	const activeUsersOnCaseIndicator = useFlag(FeatureFlagEnums.ACTIVE_USERS_ON_CASE_INDICATOR);
	const waterReportV2Flag = useFlag(FeatureFlagEnums.WATER_DAMAGE_REPORT_V2);

	const { t } = useTranslation();
	const history = useHistory();
	const { id } = useParams<{ id: string }>();

	const trackOptions: SelectOption[] = [
		{ label: '0 - Telefonvagtsag', value: '0' },
		{ label: '1', value: '1' },
		{ label: '2 - Dagligdags drift', value: '2' },
		{ label: '3', value: '3' },
		{ label: '4 - Mangeskade / Akut', value: '4' },
		{ label: '5 - Akut 2. Prioritet', value: '5' },
		{ label: '6 - Afventer (f.eks. vand skal trække tilbage)', value: '6' },
		{ label: '7 - Kun behov for tekniker', value: '7' },
		{ label: '8 - Videregivet til underleverandør', value: '8' },
		{ label: '9 - Ikke prioritetskunde', value: '9' },
		{ label: '10', value: '10' },
	];

	const [updateCase, { loading: updateCaseSubmitting }] = useMutation<UpdateCase, UpdateCaseVariables>(UPDATE_CASE);
	const [createDamageReport, { loading: creatingReport }] = useMutation<CreateWebDamageReport, CreateWebDamageReportVariables>(CREATE_DAMAGE_REPORT, {
		onCompleted: data => {
			window.open(data.createDamageReport.url, '_blank');
		},
		onError: error => {
			console.log(error);
		},
	});

	const [editMail, setEditMail] = React.useState(false);
	const [createJob, setCreateJob] = React.useState(false);
	const [viewGuides, setViewGuides] = React.useState(false);
	const [changeTrack, setChangeTrack] = React.useState(false);
	const [showCreateRequisitionModal, setShowCreateRequisitionModal] = React.useState(false);
	const [showBillingModal, setShowBillingModal] = React.useState(false);
	const [showChangesModal, setShowChangesModal] = React.useState(false);
	const [showHoursNotApprovedModal, setShowHoursNotApprovedModal] = React.useState(false);
	const [showScalepointModal, setShowScalepointModal] = React.useState(false);
	const [showMergeCaseModal, setShowMergeCaseModal] = React.useState(false);
	const [caseSearchText, setCaseSearchText] = React.useState('');
	const [selectedCase, setSelectedCase] = React.useState<GetWebCasesByErpNoQuery['cases'][number] | undefined>(undefined);

	const { loading, data: caseData } = useQuery<GetSingleCase, GetSingleCaseVariables>(GET_CASE, {
		context: { debatch: true },
		fetchPolicy: 'cache-and-network',
		variables: {
			id: id,
			includeEconomy: true,
		},
		skip: !id,
	});

	const [getMergeCaseDataRaw] = useLazyQuery<GetSingleCase, GetSingleCaseVariables>(GET_CASE, {
		context: { debatch: true },
		fetchPolicy: 'cache-and-network',
	});

	const { data: hoursInBcNotApprovedData } = useGetTimeTrackingsInBcWithoutApprovalQuery({
		variables: {
			erpNo: caseData?.case?.erpNo ?? '',
		},
		skip: !showNonApprovedHoursOnCase || loading,
	});

	const { data: duplicatedCasesData } = useGetDuplicateCasesQuery({
		variables: caseData && {
			caseId: caseData.case.id,
			address: removeTypename(caseData.case.damage.contact.address),
			damageCategory: caseData.case.damage.category.id,
			// Return connected scalepoint cases if the current scalepoint url is empty
			// Otherwise return not connected scalepoint cases (if the current case HAS a scalepoint url)
			hasScalepointUrl: caseData.case.scalePointUrl ? false : true,
		},
		skip: !scalepointMergeCases || !caseData,
		// Always fetch duplicate cases
		fetchPolicy: 'no-cache',
		nextFetchPolicy: 'no-cache',
	});

	const [mergeCases, { loading: mergeCasesLoading }] = useMergeCasesMutation();

	const handleMergeCases = async (duplicateCaseId: string) => {
		if (!caseData?.case?.id) return;
		const caseToMerge = await getMergeCaseDataRaw({
			variables: {
				id: duplicateCaseId,
				includeEconomy: false,
			},
		});
		//console.log('caseToMerge', caseToMerge);
		const canCloseCase = caseToMerge.data?.case ? !isCaseValidToClose(caseToMerge.data.case, true).invalidForClose : false;

		if (!canCloseCase) {
			const shouldMerge = window.confirm(
				`${caseToMerge.data?.case.erpNo} kunne ikke lukkes da der er forbrug på sagen.
				\n\nSørg for at det registreret forbrug bliver flyttet over på den sag som det videre arbejde skal køres på.
				\n\nØnsker du fortsat at flette og lade ${caseToMerge.data?.case.erpNo} forblive åben?`,
			);
			if (!shouldMerge) return;
		}

		try {
			await mergeCases({
				variables: {
					primaryCaseId: caseData.case.id,
					duplicateCaseId,
					shouldCloseDuplicateCase: canCloseCase,
				},
				onCompleted: () => {
					setShowScalepointModal(false);
					setShowMergeCaseModal(false);
				},
			});
			//setSelectedCase(undefined);
		} catch (error) {
			console.error('Failed to merge cases:', error);
			//setSelectedCase(undefined);
		}
	};

	const dummyForm = useForm();

	const { erpNoCasesRaw } = useWebCasesByErpNoQuery(caseSearchText, false, false);
	const searchedCases = React.useMemo(() => {
		return [
			...(erpNoCasesRaw?.cases
				.filter(c => c.timeRegistration)
				.map(
					(c): SelectOption => ({
						label: `${c.erpNo} - ${c.damage.contact.address.road} ${c.damage.contact.address.houseNumber}, ${c.damage.contact.address.postalCode} ${c.damage.contact.address.city} (${c.damage.category.name}/${c.damage.cause.name})`,
						value: c.id,
					}),
				) ?? []),
		];
	}, [erpNoCasesRaw]);

	if (loading && typeof caseData === 'undefined') {
		return <Loading title="case.gettingCase" />;
	}

	if (typeof caseData === 'undefined') {
		return <p>Error</p>;
	}

	const changeCaseStatus = async (status: CaseStatus) => {
		await updateCase({
			variables: {
				id: caseData.case.id,
				status: status,
			},
		});
	};

	const changeScalePointStatus = async (status: ScalePointStatus) => {
		await updateCase({
			variables: {
				id: caseData.case.id,
				scalePointStatus: status,
			},
		});
	};

	if (userContext.user === null) {
		return <NoUser />;
	}

	const showWaterReportV2 = waterReportV2Flag && (DateTime.fromISO(caseData.case.createdAt).toISODate() ?? '') >= '2026-10-26';

	return (
		<div>
			{activeUsersOnCaseIndicator && <ActiveUsersOnCaseIndicator id={caseData.case.id} />}
			<Header>
				<div className="text-blue flex text-sm lg:flex-row">
					<div className="mr-14 flex whitespace-nowrap">
						<div className="mt-2 flex h-6 flex-row items-center">
							<FavoriteToggle
								userFavoriteColor={caseData.case.userFavorite?.active === true ? caseData.case.userFavorite?.color ?? '' : ''}
								caseId={caseData.case.id}
								wrapperClass="mx-2"
							/>
						</div>
						<div className="flex flex-col gap-y-1 py-2">
							<div className="flex h-6 flex-row items-center font-bold">
								<h1 className="text-xl">{t('case.caseNo') + caseData.case.erpNo}</h1>
							</div>
							<div className="text-sm">
								{t('common.created')} <span>{dateToDateTimeString(caseData.case.createdAt)}</span>
							</div>
							<div className="flex flex-row text-sm">
								<button onClick={() => setChangeTrack(true)} className="text-left whitespace-normal overflow-ellipsis overflow-hidden">
									{t('case.track')}: <span className="font-semibold">{trackOptions.find(t => t.value === caseData.case.track.toString())?.label}</span>
								</button>
							</div>
							<div>
								<AdministrativeProgress
									caseData={{
										...caseData.case,
										caseEconomics: null,
									}}
								/>
								<hr />
								<OperationProgress
									caseData={{
										...caseData.case,
										caseEconomics: null,
									}}
								/>
							</div>
						</div>
					</div>

					<div>
						<div className="mr-4 flex flex-row flex-wrap justify-between items-top gap-y-3 py-3">
							<div className="flex flex-row flex-wrap">
								<RestrictedArea permissions={[Permissions.CASES_CREATE]}>
									{!caseViewExternal && <TextButton className="mr-3" link text="case.duplicate" icon={faPlus} onClick={() => history.push(`/casecreation/${id}`)} />}
								</RestrictedArea>

								<RestrictedArea permissions={[Permissions.CASES_EMAIL_EDIT]}>
									<TextButton className="mr-3" text="case.sendCaseMail" icon={faEnvelope} onClick={() => setEditMail(true)} />
								</RestrictedArea>

								<TextButton className="mr-3" link text="case.documentsAndPictures" icon={faImages} onClick={() => history.push(`/case/${id}/files`)} />

								<TextButton className="mr-3" link text="case.seeGuides" icon={faFile} onClick={() => setViewGuides(true)} />

								<GoogleMapsLink address={caseData.case.damage.contact.address}>
									<TextButton link text="case.seeCaseOnMap" icon={faMapMarkerAlt} />
								</GoogleMapsLink>
							</div>
							{!isExternalUser && (hoursInBcNotApprovedData?.timeTrackingsInBcWithoutApproval ?? []).length > 0 && (
								<div>
									<TextButton className="text-orange" text="timeRegistration.hoursNotApprovedOnCase" icon={faExclamationCircle} onClick={() => setShowHoursNotApprovedModal(true)} />
								</div>
							)}
						</div>

						<div className="mr-4 flex flex-row flex-wrap items-baseline gap-y-3 py-3">
							{enableOfferModule && <TextButton className="mr-3" link text="common.offer" icon={faPlus} onClick={() => history.push(`/case/${id}/offers`)} />}
							{!caseViewExternal && (
								<>
									<TextButton
										className="mr-3"
										text="requisitions.createRequisition"
										icon={faPlus}
										onClick={() => setShowCreateRequisitionModal(true)}
										disabled={caseData.case.status !== CaseStatus.OPEN}
										tooltip={caseData.case.status !== CaseStatus.OPEN ? 'case.featureTurnedOff' : undefined}
									/>

									<TextButton className="mr-3" text="common.createTask" icon={faPlus} onClick={() => setCreateJob(true)} />
								</>
							)}

							<RestrictedArea permissions={[Permissions.MOVABLES_VIEW]}>
								<MovablesOverviewModal caseId={caseData.case.id} erpNo={caseData.case.erpNo} />
								<MovablesOverviewDownload caseId={caseData.case.id} erpNo={caseData.case.erpNo} />
							</RestrictedArea>
						</div>

						<div className="mr-4 flex flex-row flex-wrap items-top gap-y-3 py-3">
							<RestrictedArea permissions={[Permissions.CASES_ECONOMY_INVOICES_EDIT]}>
								{!caseViewExternal && !caseData.case.debitor.automaticTrackOneInvoicing && (
									<Popover
										placement="bottom"
										content={
											<div className="rounded-b-default border-1 shadow-default border-black bg-white p-2 text-xs">
												<p>
													{caseData.case.debitor.company} {t('case.invoice.noPoliceNo')}
												</p>
											</div>
										}
										visible={!caseData.case.debitor.allowInvoicingWithoutReqNo && caseData.case.debitor.policeNumber.length === 0 ? undefined : false}
									>
										<div>
											<TextButton
												className="mr-3"
												text="case.invoice.link"
												icon={faFileInvoice}
												disabled={
													(!caseData.case.debitor.allowInvoicingWithoutReqNo && caseData.case.debitor.policeNumber.length === 0) ||
													caseData.case.status !== CaseStatus.OPEN ||
													caseData.case.debitor.blocked
												}
												tooltip={caseData.case.status !== CaseStatus.OPEN ? 'case.featureTurnedOff' : undefined}
												onClick={() => setShowBillingModal(true)}
											/>
										</div>
									</Popover>
								)}
							</RestrictedArea>
							{!caseViewExternal && (
								<TextButton
									className="mr-3"
									text={caseData.case.timeRegistration ? t('case.stopTimeRegistration') : t('case.startTimeRegistration')}
									icon={caseData.case.timeRegistration ? faPause : faPlay}
									onClick={() =>
										updateCase({
											variables: {
												id: caseData.case.id,
												timeRegistration: !caseData.case.timeRegistration,
											},
											update: (cache, { data: cacheData }): void => {
												if (typeof cacheData === 'undefined' || cacheData === null || cacheData.updateCase === null) {
													return;
												}

												const cachedRequest = cache.readQuery<GetSingleCase, GetSingleCaseVariables>({
													query: GET_CASE,
													variables: {
														id: caseData.case.id,
														includeEconomy: true,
													},
												});

												if (cachedRequest === null || cachedRequest.case === null) {
													return;
												}

												cache.writeQuery<GetSingleCase, GetSingleCaseVariables>({
													query: GET_CASE,
													variables: {
														id: caseData.case.id,
														includeEconomy: true,
													},
													data: {
														case: {
															...cachedRequest.case,
															...cacheData.updateCase,

															debitor: {
																...cachedRequest.case.debitor,
																...cacheData.updateCase.debitor,
																automaticTrackOneInvoicing: caseData.case.debitor.automaticTrackOneInvoicing,
															},
														},
													},
												});
											},
										})
									}
								/>
							)}
							<CloseCaseButton
								caseObj={caseData.case}
								caseStatus={caseData.case.status}
								caseOrProjectManager={
									caseData.case.projectManager?.id === userContext.user.id ||
									caseData.case.caseManager?.id === userContext.user.id ||
									caseData.case.projectManager?.proxy?.id === userContext.user.id
								}
								changeCaseStatus={changeCaseStatus}
								loading={updateCaseSubmitting}
							/>

							<ConnectToScalepointButton
								caseObj={caseData.case}
								caseOrProjectManager={
									caseData.case.projectManager?.id === userContext.user.id ||
									caseData.case.caseManager?.id === userContext.user.id ||
									caseData.case.projectManager?.proxy?.id === userContext.user.id
								}
								changeScalepointStatus={changeScalePointStatus}
								loading={updateCaseSubmitting}
							/>

							<RestrictedArea permissions={[Permissions.CASES_OPEN]}>
								{caseData.case.status === CaseStatus.CLOSED && <ReopenCaseButton changeCaseStatus={changeCaseStatus} loading={updateCaseSubmitting} />}
							</RestrictedArea>

							{caseData.case.scalePointUrl !== null && (
								<TextButton className="ml-3" link text="case.scalePoint" icon={faDotCircle} onClick={() => window.open(caseData.case.scalePointUrl ?? '', '_blank')} />
							)}

							{scalepointMergeCases && caseData.case.status !== CaseStatus.CLOSED && caseData.case.scalePointStatus !== ScalePointStatus.CONNECTED && (
								<TextButton
									className="ml-3"
									link
									text="Flet Scalepoint sag"
									icon={faRandom}
									onClick={() => setShowMergeCaseModal(true)}
									disabled={caseData.case.scalePointUrl ? true : false}
								/>
							)}

							<TextButton className="ml-3" link text="case.incendium" onClick={() => window.open(envVar.incendiumUrl, '_blank')} />

							{!caseData.case.erpNo.startsWith('DK') && (
								<div className="ml-3 flex self-end">
									<a href={`https://ssg5c.dk/sagsoversigt/showcase.asp?id=${caseData.case.erpNo}`} target="_blank" rel="noreferrer" className="underline">
										{t('case.caseInOld5C')}
									</a>
								</div>
							)}
						</div>
						{!caseViewExternal && (
							<>
								<div className="mr-4 flex flex-row flex-wrap gap-y-3 py-3">
									<TextButton
										className="mr-3"
										text="common.createDamageReport"
										disabled={creatingReport}
										loading={creatingReport}
										icon={faPlus}
										onClick={() =>
											createDamageReport({
												variables: { id },
											})
										}
									/>

									<TextButton className="mr-3" text="common.createInspectionReport" icon={faPlus} link onClick={() => history.push(`/case/${id}/inspection-report`)} />
									{inspectionMovablesReportFlag && (
										<TextButton
											className="mr-3"
											text="common.createInspectionMovablesReport"
											icon={faPlus}
											link
											onClick={() => history.push(`/case/${id}/inspection-report-movables`)}
										/>
									)}

									<TextButton className="mr-3" text="common.createTrygWaterDamageReport" icon={faPlus} link onClick={() => history.push(`/case/${id}/tryg-water-report`)} />

									{showWaterReportV2 ? (
										<TextButton className="mr-3" text="common.createWaterDamageReportV2" icon={faPlus} link onClick={() => history.push(`/case/${id}/water-report-v2`)} />
									) : (
										<TextButton className="mr-3" text="common.createWaterDamageReport" icon={faPlus} link onClick={() => history.push(`/case/${id}/water-report`)} />
									)}

									<TextButton className="mr-3" text="common.createIndoorClimateReport" icon={faPlus} link onClick={() => history.push(`/case/${id}/indoor-climate-report`)} />

									{(caseData.case.version > 1 || caseData.case.changes.length > 0) && (
										<TextButton text={t('case.caseChangesView')} icon={faHistory} className="mr-3" onClick={() => setShowChangesModal(true)} />
									)}

									<RelatedCatalogs
										caseData={{
											...caseData.case,
											caseEconomics: null,
											caseReports: null,
										}}
									/>
								</div>
								<div className="mr-4 flex flex-row flex-wrap gap-y-3 py-3">
									{asbestosReportFlag && caseData.case.damage.cause.name === 'Asbest' && (
										<>
											<TextButton
												className="mr-3"
												text="common.createNoticeOfConstructionWaste"
												icon={faPlus}
												link
												onClick={() => history.push(`/case/${id}/notice-of-constructionwaste-report`)}
											/>

											<TextButton
												className="mr-3"
												text="common.createInspectionAssesmentReport"
												icon={faPlus}
												link
												onClick={() => history.push(`/case/${id}/inspection-assesment-report`)}
											/>

											<TextButton className="mr-3" text="common.createEndevaluationReport" icon={faPlus} link onClick={() => history.push(`/case/${id}/endevaluation-report`)} />

											<TextButton className="mr-3" text="common.createDeviationReport" icon={faPlus} link onClick={() => history.push(`/case/${id}/deviation-report`)} />

											<TextButton
												className="mr-3"
												text="common.createCaseOrganizationReport"
												icon={faPlus}
												link
												onClick={() => history.push(`/case/${id}/case-organization-report`)}
											/>
										</>
									)}
								</div>
							</>
						)}
					</div>
				</div>
			</Header>

			{caseData.case.status === CaseStatus.APPLIED_CLOSED && <div className="bg-orange mb-8 -mt-8 w-full py-1 text-center text-white">{t('case.caseAppliedComplete')}</div>}

			{caseData.case.status === CaseStatus.CLOSED && <div className="bg-green mb-8 -mt-8 w-full py-1 text-center text-white">{t('case.caseComplete')}</div>}

			{caseData.case.status !== CaseStatus.CLOSED &&
				caseData.case.changes.some(({ before, after }) => before.debitor !== null && after.debitor !== null && before.debitor.debitorId !== after.debitor.debitorId) && (
					<div className="bg-orange mb-8 -mt-8 w-full py-1 text-center text-white">{t('case.debitorChanged')}</div>
				)}

			{caseData.case.debitor.blocked && <div className="bg-orange mb-8 -mt-8 w-full py-1 text-center text-white">Debitor er spærret eller udgået</div>}

			{scalepointMergeCases && caseData.case.status !== CaseStatus.CLOSED && caseData.case.scalePointMergeInfo === null && (duplicatedCasesData?.duplicateCases?.length ?? 0) > 0 && (
				<div className="bg-orange mb-8 -mt-8 w-full py-1 text-center text-white">
					En lignende sag findes allerede med Scalepoint link:
					<button type="button" className="underline text-white bg-transparent border-none p-0 m-0 cursor-pointer ml-2" onClick={() => setShowScalepointModal(true)}>
						Flet sag
					</button>
				</div>
			)}

			<BoxContainer>
				<div className="w-full lg:w-2/3">
					<div className="mb-8 flex w-full flex-row">
						<div className="w-full lg:w-1/2 lg:flex-col">
							<InjuredPartyBox caseData={caseData.case} caseViewExternal={caseViewExternal} />

							<CustomPolicyHolderBox caseData={caseData.case} caseViewExternal={caseViewExternal} />

							<AdviserReferrerBox caseData={caseData.case} caseViewExternal={caseViewExternal} />

							{!userContext.user?.external && <ImportantInfoBox caseData={caseData.case} />}
						</div>

						<div className=" w-full lg:w-1/2 lg:flex-col">
							<DebitorBox caseData={caseData.case} caseViewExternal={caseViewExternal} />

							<DamageBox caseData={caseData.case} caseViewExternal={caseViewExternal} />

							<DamageContactBox caseData={caseData.case} caseViewExternal={caseViewExternal} />
						</div>
					</div>
				</div>

				<div className="mb-8 flex w-full flex-row lg:w-1/3 lg:flex-col">
					<SSGInformationBox caseData={caseData.case} caseViewExternal={caseViewExternal} />

					<SkaforBox caseData={caseData.case} caseViewExternal={caseViewExternal} />

					<VisitationBox caseData={caseData.case} caseViewExternal={caseViewExternal} />
				</div>

				<div className="w-1/2">
					<CaseAgreementsBox caseData={caseData.case} caseViewExternal={caseViewExternal} />
				</div>

				<div className="w-1/2">
					<DrivingSlipBox caseData={caseData.case} caseViewExternal={caseViewExternal} />
				</div>

				<ScreeningBox
					debitor={caseData.case.debitor} // There Should be a global state for the application to prevent props drilling
					caseId={caseData.case.id}
					caseCreated={DateTime.fromISO(caseData?.case.createdAt ?? '2000-01-01')}
					caseErpNo={caseData.case.erpNo}
					defaultTemplate={caseData.case.debitor.defaultScreeningTemplate ?? undefined}
					caseViewExternal={caseViewExternal}
					caseBlocked={caseData.case.debitor.blocked}
				/>

				<MiscBox caseData={caseData.case} />

				{!caseViewExternal && (
					<CaseUsageBox
						caseErpNo={caseData.case.erpNo}
						caseViewExternal={caseViewExternal}
						drivingSlipsWithMaterials={caseData.case.drivingSlipSeries.flatMap(series => series.drivingSlips).filter(ds => ds.materials.length > 0)}
					/>
				)}

				<RiskEvaluationBox caseData={caseData.case} caseViewExternal={caseViewExternal} />
			</BoxContainer>

			{editMail && (
				<CaseMail
					open={editMail}
					close={() => setEditMail(false)}
					data={caseData.case}
					config={{
						subject: `${t('mail.defaultSubject', {
							erpNo: caseData.case.erpNo,
							policeNumber: caseData.case.debitor.policeNumber,
						})}`,
						recipients: [
							{
								email: caseData.case.damage.contact.email,
								role: 'case.injuredParty',
								name: caseData.case.damage.contact.name,
							},
							{
								email: caseData.case.debitor.email ?? '',
								role: 'common.debitor',
								name: caseData.case.debitor.company,
							},
							{
								email: caseData.case.referrer?.email ?? '',
								role: 'case.referrer',
								name: caseData.case.referrer?.name ?? '',
							},
							{
								email: caseData.case.policyHolder.email,
								role: 'case.policyHolder',
								name: caseData.case.policyHolder.name,
							},
							{
								email: caseData.case.requisitioner.email,
								role: 'case.requisitioner',
								name: caseData.case.requisitioner.name,
							},
							{
								email: caseData.case.adviser?.email ?? '',
								role: 'case.adviser',
								name: caseData.case.adviser?.name ?? '',
							},
							{
								email: caseData.case.projectManager?.email ?? '',
								role: 'case.ssgProjectManager',
								name: caseData.case.projectManager?.email ?? '',
								proxy: caseData.case.projectManager?.proxy
									? {
											label: caseData.case.projectManager.proxy.name,
											value: caseData.case.projectManager.proxy.email,
									  }
									: undefined,
							},
							...(caseData.case.projectManager?.proxy
								? [
										{
											email: caseData.case.projectManager.proxy.email,
											role: 'myPage.proxyTitle',
											name: caseData.case.projectManager.proxy.name,
											readonly: true,
										},
								  ]
								: []),
							{
								email: caseData.case.caseManager?.email ?? '',
								role: 'case.ssgCaseManager',
								name: caseData.case.caseManager?.email ?? '',
							},
							...caseData.case.damage.contacts.map(c => {
								return {
									email: c.email ?? '',
									role: c.role,
									name: c.name,
								};
							}),
						],
						message: `\nMVH.\n\n${userContext.user.name}`,
						defaultTabIndex: 0,
						fileNames: [],
					}}
				/>
			)}
			{createJob && <CreateJobModal open={createJob} close={() => setCreateJob(false)} caseData={caseData.case} />}
			{viewGuides && <ViewGuides open={viewGuides} close={() => setViewGuides(false)} />}
			{changeTrack && <ChangeTrack caseId={caseData.case.id} currentTrack={caseData.case.track} open={changeTrack} close={() => setChangeTrack(false)} />}

			{showBillingModal && (
				<InvoiceModal
					caseId={caseData.case.id}
					caseNo={caseData.case.erpNo}
					caseCreated={DateTime.fromISO(caseData.case.createdAt)}
					debitor={caseData.case.debitor}
					track={caseData.case.track}
					assortmentCode={caseData.case.debitor?.assortmentCode ?? ''}
					visible={showBillingModal}
					close={() => setShowBillingModal(false)}
				/>
			)}

			{showChangesModal && <CaseChangeLog version={caseData.case.version} changes={caseData.case.changes} visible={showChangesModal} close={() => setShowChangesModal(false)} />}
			{showCreateRequisitionModal && <RequisitionModal visible={showCreateRequisitionModal} close={() => setShowCreateRequisitionModal(false)} caseData={caseData.case} />}
			<Modal
				title="timeRegistration.noApprovedHours"
				visible={showHoursNotApprovedModal}
				close={() => setShowHoursNotApprovedModal(false)}
				size={ModalSize.LARGE}
				body={
					<>
						<Table
							data={[...(hoursInBcNotApprovedData?.timeTrackingsInBcWithoutApproval ?? [])].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())}
							keySelector={t => t.id}
							noDataFoundText={`${t('common.none')} ${t('timeRegistration.noApprovedHours').toLowerCase()}`}
							columns={[
								{
									label: 'common.date',
									selectFn: t => formatDateForInputEU(new Date(t.startDate)),
								},
								{
									label: 'common.name',
									selectFn: t => t.user.name,
								},
								{
									label: 'common.employeeResponsibleStatus',
									selectFn: t => {
										const statusMap: Record<string, string> = {
											OPEN: 'Dag åben',
											APPROVED: 'Godkendt',
											REJECTED: 'Afvist',
											AWAITING: 'Afventer godkendelse',
											SUBMITTED: 'Afventer godkendelse',
										};
										return statusMap[t.emploeeResponsible] || t.emploeeResponsible;
									},
								},
								{
									label: 'common.projectResponsibleStatus',
									selectFn: t => {
										const statusMap: Record<string, string> = {
											OPEN: 'Dag åben',
											APPROVED: 'Godkendt',
											REJECTED: 'Afvist',
											AWAITING: 'Afventer godkendelse',
											SUBMITTED: 'Afventer godkendelse',
										};
										return statusMap[t.projectApprovalStatus] || t.projectApprovalStatus;
									},
								},
								{
									label: 'timeRegistration.task',
									selectFn: t => t?.jobTaskName ?? '',
								},
								{
									label: 'common.hours',
									selectFn: t => t.hours,
								},
								{
									label: 'timeRegistration.log.startTime',
									selectFn: t => t.startTime,
								},
								{
									label: 'timeRegistration.log.endTime',
									selectFn: t => t.endTime,
								},
							]}
						/>
					</>
				}
			/>

			<Modal
				title="Scalepoint"
				visible={showScalepointModal}
				close={() => setShowScalepointModal(false)}
				size={ModalSize.LARGE}
				body={
					<>
						<p>Mulig duplikerede sag(er)</p>
						<Table
							data={duplicatedCasesData?.duplicateCases ?? []}
							keySelector={c => c.id}
							noDataFoundText={'Ingen duplikerede sager fundet'}
							columns={[
								{
									label: 'common.date',
									selectFn: c => formatDateForInputEU(new Date(c.createdAt)),
								},
								{
									label: 'case.caseNo',
									selectFn: c => c.erpNo,
								},
								{
									label: 'common.address',
									selectFn: c => c.damage.contact.address.addressLine ?? '',
								},
								{
									label: 'common.damageCategory',
									selectFn: c => c.damage.category.name,
								},
								{
									label: 'case.injuredParty',
									selectFn: c => c.damage.contact.name,
								},
								{
									label: '',
									selectFn: c => (
										<div className="flex space-x-2 justify-end">
											<Button
												primary
												text="Gå til sag"
												onClick={() => {
													setShowScalepointModal(false);
													history.push(`/case/${c.id}`);
												}}
											/>
											<Button
												danger
												text="Flet sag"
												title="Flet denne sag med den viste sag"
												onClick={() => {
													const confirm = window.confirm(
														`Er du sikker på, at du vil flette denne sag? Sagen vil blive lukket, og Scalepoint data vil blive overført til ${caseData.case.erpNo}.`,
													);
													if (confirm) {
														//setSelectedCase(c);
														handleMergeCases(c.id);
														setShowScalepointModal(false);
													}
												}}
												// Disable button if the case we are on is already scalepoint connected
												disabled={mergeCasesLoading || caseData.case.scalePointUrl !== null}
											/>
										</div>
									),
								},
							]}
						/>
					</>
				}
			/>

			<Modal
				title="Flet Scalepoint sag manuelt"
				visible={showMergeCaseModal}
				close={() => setShowMergeCaseModal(false)}
				size={ModalSize.MEDIUM}
				body={
					<div>
						<div style={{ minHeight: '220px' }}>
							<div className="flex justify-between items-center space-x-4">
								<p className="w-2/6 font-semibold">{caseData.case.erpNo}</p>
								<p className="w-1/6">
									<FontAwesomeIcon icon={faArrowLeft} />
								</p>
								<div className="w-3/6">
									<SearchableSelect
										control={dummyForm.control}
										name="case"
										title="common.case"
										options={searchedCases.filter(c => c.label.toLowerCase().includes(caseSearchText.toLowerCase()))}
										searchFn={searchText => setCaseSearchText(searchText)}
										onSelect={value => {
											const caseItem = erpNoCasesRaw?.cases.find(c => c.id === value) ?? undefined;
											//console.log('caseItem', caseItem);
											setSelectedCase(caseItem);
										}}
										required
										onBlur={() => undefined}
										minInputLength={2}
										isLoading={false}
										placeholder="Søg efter sag..."
									/>
								</div>
							</div>
							{selectedCase !== undefined && (
								<div className="text-right mt-2">
									<p>{`${selectedCase.damage.contact.address.road} ${selectedCase.damage.contact.address.houseNumber}, ${selectedCase.damage.contact.address.postalCode} ${selectedCase.damage.contact.address.city} - ${selectedCase.damage.category.name} - ${selectedCase.damage.contact.name}`}</p>
								</div>
							)}
						</div>

						<div className="flex space-x-2 justify-end">
							<Button
								primary
								text="Afbryd"
								onClick={() => {
									setShowMergeCaseModal(false);
								}}
							/>
							<Button
								danger
								text="Flet sag"
								title="Flet denne sag med den viste sag"
								onClick={() => {
									const confirm = window.confirm(
										`Er du sikker på, at du vil flette denne sag? Sagen vil blive lukket, og Scalepoint data vil blive overført til ${caseData.case.erpNo}.`,
									);
									if (confirm && selectedCase) {
										handleMergeCases(selectedCase.id);
										setSelectedCase(undefined);
									}
								}}
								// Disable button if the case we are on is already scalepoint connected
								disabled={mergeCasesLoading || caseData.case.scalePointUrl !== null}
							/>
						</div>
					</div>
				}
			/>
		</div>
	);
};

export default SingleCase;

